/**
 * useDocViewerNavigation Hook
 * Manages document viewer navigation history similar to FSExplorer
 * Tracks navigation source and allows returning to previous location with browser path sync
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface NavigationHistoryEntry {
  path: string;
  timestamp: number;
  referrer?: string;
}

interface DocViewerNavigationState {
  canGoBack: boolean;
  currentPath: string;
  previousPath: string | null;
  navigationHistory: NavigationHistoryEntry[];
}

interface DocViewerNavigationActions {
  goBack: () => void;
  recordNavigation: (previousPath: string) => void;
  clearHistory: () => void;
}

const STORAGE_KEY = 'docviewer_navigation_history';
const MAX_HISTORY_ENTRIES = 50;

/**
 * Custom hook for DocumentViewer navigation with browser history support
 * Similar to FSExplorer's useExplorerNavigation
 */
export function useDocViewerNavigation(): [DocViewerNavigationState, DocViewerNavigationActions] {
  const navigate = useNavigate();
  const location = useLocation();

  // Local state
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryEntry[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load navigation history from storage:', error);
      return [];
    }
  });

  const [currentPath, setCurrentPath] = useState(location.pathname);
  const isNavigatingRef = useRef(false);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Keep navigation history synced with localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(navigationHistory));
    } catch (error) {
      console.warn('Failed to save navigation history:', error);
    }
  }, [navigationHistory]);

  // Update current path when location changes
  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Record a new navigation entry
   * @param previousPath - The path user navigated from
   */
  const recordNavigation = useCallback((previousPath: string) => {
    if (isNavigatingRef.current) return;

    if (previousPath === currentPath) return;

    const newEntry: NavigationHistoryEntry = {
      path: previousPath,
      timestamp: Date.now(),
      referrer: document.referrer || previousPath
    };

    setNavigationHistory((prev) => {
      const oneHourAgo = Date.now() - (60 * 60 * 1000);
      const filtered = prev.filter((entry) => entry.timestamp > oneHourAgo);

      const pathExists = filtered.some((entry) => entry.path === newEntry.path);
      if (pathExists) {
        return filtered;
      }

      return [newEntry, ...filtered].slice(0, MAX_HISTORY_ENTRIES);
    });
  }, [currentPath]);

  /**
   * Navigate back to previous location
   */
  const goBack = useCallback(() => {
    if (navigationHistory.length === 0) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate('/explorer');
      }
      return;
    }

    if (isNavigatingRef.current) {
      console.warn('Navigation already in progress, ignoring back request');
      return;
    }

    isNavigatingRef.current = true;

    try {
      const previousEntry = navigationHistory.find(
        (entry) => entry.path !== currentPath
      );

      if (previousEntry && previousEntry.path) {
        setNavigationHistory((prev) =>
          prev.filter((entry) => entry.path !== previousEntry.path)
        );

        navigate(previousEntry.path);
        window.history.pushState(
          { navigationSource: 'docviewer' },
          '',
          previousEntry.path
        );

        navigationTimeoutRef.current = setTimeout(() => {
          isNavigatingRef.current = false;
        }, 100);
      } else {
        isNavigatingRef.current = false;
        navigate('/explorer');
      }
    } catch (error) {
      console.error('Error during back navigation:', error);
      isNavigatingRef.current = false;

      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate('/explorer');
      }
    }
  }, [navigationHistory, currentPath, navigate]);

  /**
   * Clear navigation history
   */
  const clearHistory = useCallback(() => {
    setNavigationHistory([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear navigation history:', error);
    }
  }, []);

  const previousPath = navigationHistory.find(
    (entry) => entry.path !== currentPath
  )?.path || null;
  const canGoBack = navigationHistory.length > 0;

  // State object
  const state: DocViewerNavigationState = {
    canGoBack,
    currentPath,
    previousPath,
    navigationHistory
  };

  // Actions object
  const actions: DocViewerNavigationActions = {
    goBack,
    recordNavigation,
    clearHistory
  };

  return [state, actions];
}

export default useDocViewerNavigation;
